import { Dynamic } from "solid-js/web";
import { TreePine } from "lucide-solid";
import { cn } from "@famidex/shared";

/**
 * FamiDex brand mark — Forest Nurture identity (Stitch export): a pine-tree
 * glyph on a solid accent chip + the "FamiDex" serif wordmark. The chip uses
 * bg-accent + text-accent-foreground (theme-safe pattern: legible on any
 * theme×mode — forest green on cream in light, mossy mint on Night Moss in
 * dark), instead of a loose text-accent glyph.
 */
function LogoMark(props: { class?: string }) {
  return (
    <span
      class={cn(
        "grid shrink-0 place-items-center rounded-[28%] bg-accent text-accent-foreground",
        props.class,
      )}
      aria-hidden="true"
    >
      <TreePine class="h-[62%] w-[62%]" stroke-width={2.2} />
    </span>
  );
}

interface LogoProps {
  size?: "sm" | "lg";
  class?: string;
  /**
   * Element used for the wordmark in the `lg` variant. Defaults to `h1`, but
   * auth cards pass `p` so the page keeps a single brand `h1` (the auth brand
   * panel) and the cards expose their own `h2` heading instead.
   */
  as?: "h1" | "p";
}

export function Logo(props: LogoProps) {
  const size = () => props.size ?? "sm";
  const wordmarkTag = () => props.as ?? "h1";

  return (
    <div
      class={cn(
        "flex items-center gap-2.5 select-none",
        size() === "lg" && "flex-col gap-4 text-center",
        props.class,
      )}
    >
      {size() === "sm" ? (
        <>
          <LogoMark class="h-8 w-8 drop-shadow-sm" />
          <span class="text-xl font-bold tracking-tight text-foreground">FamiDex</span>
        </>
      ) : (
        <>
          <LogoMark class="h-16 w-16 mb-2 drop-shadow-md transition-transform hover:scale-105 active:scale-95 duration-300" />
          <Dynamic
            component={wordmarkTag()}
            class="text-3xl font-bold tracking-tight text-foreground"
          >
            FamiDex
          </Dynamic>
        </>
      )}
    </div>
  );
}
